import cx from "classnames";
import { faArrowRight, faRepeatAlt } from "@fortawesome/pro-regular-svg-icons";

import Icon from "common/components/Icon";
import LinkButton from "common/components/LinkButton";
import { toLocaleDate } from "common/utils/dates";

import { ProductCardBadgePrice } from "client/products/types";

import { StorefrontCollectionRebate } from "../types";

import styles from "../styles/CollectionRebateCardList.module.less";

type CollectionRebateCardListProps = {
  rebates: StorefrontCollectionRebate[];
};

type CollectionRebateCardProps = {
  rebate: StorefrontCollectionRebate;
  index: number;
};

const isExternalUrl = (url: string): boolean => /^https?:\/\//i.test(url);

/** The card's destination, or nowhere if it is not a plain path or an http(s) URL. */
const safeUrl = (url: string): string => {
  if (url.startsWith("//")) {
    return "";
  }
  return url.startsWith("/") || isExternalUrl(url) ? url : "";
};

/** Colours defined in the stylesheet; positions past the last one start over. */
const CARD_TONE_COUNT = 5;

const CollectionRebateCard = ({ rebate, index }: CollectionRebateCardProps) => {
  const url = safeUrl(rebate.url);
  const className = cx(styles.card, styles[`tone${index % CARD_TONE_COUNT}`]);
  const card = (
    <>
      <div className={styles.body}>
        <div className={styles.meta}>
          {!!rebate.chip && (
            <span className={styles.chip}>
              {rebate.applies_to === ProductCardBadgePrice.Subscription && (
                <Icon icon={faRepeatAlt} title="Autoship" />
              )}
              {rebate.chip}
            </span>
          )}
          {!!rebate.brand && <span className={styles.brand}>{rebate.brand}</span>}
        </div>
        <div className={styles.title}>{rebate.title}</div>
        {rebate.promo_code ? (
          <div className={styles.code}>
            <span className={styles.codeLabel}>Use code</span>
            <span className={styles.codeValue}>{rebate.promo_code}</span>
          </div>
        ) : (
          <div className={styles.applied}>Applied automatically at checkout</div>
        )}
        {!!rebate.end_date && (
          <div className={styles.validity}>
            Valid through {toLocaleDate(rebate.end_date, { month: "short", day: "numeric" })}
          </div>
        )}
      </div>
      <Icon className={styles.arrow} icon={faArrowRight} />
    </>
  );
  return isExternalUrl(url) ? (
    <LinkButton className={className} href={url}>
      {card}
    </LinkButton>
  ) : (
    <LinkButton className={className} dest={{ to: url }}>
      {card}
    </LinkButton>
  );
};

/** The page's highlighted rebates as a row of cards under the hero; nothing when there are none. */
const CollectionRebateCardList = ({ rebates }: CollectionRebateCardListProps) =>
  rebates.length ? (
    <div className={styles.list}>
      {rebates.map((rebate, index) => (
        <CollectionRebateCard key={rebate.id} rebate={rebate} index={index} />
      ))}
    </div>
  ) : null;

export default CollectionRebateCardList;
